import type { AnyValue } from "@/lib/rowTypes";
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export interface Mission {
  id: string;
  mission_key: string;
  mission_type: "daily" | "weekly";
  title: string;
  description: string;
  icon: string;
  target_count: number;
  current_count: number;
  xp_reward: number;
  coin_reward: number;
  status: "active" | "completed" | "claimed" | "expired";
  assigned_at: string;
  expires_at: string;
  claimed_at: string | null;
}

export function useMissions() {
  const { user } = useAuth();
  const [missions, setMissions] = useState<Mission[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchMissions = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from("user_missions")
      .select("*")
      .eq("user_id", user.id)
      .gte("expires_at", new Date().toISOString())
      .order("mission_type", { ascending: true })
      .order("assigned_at", { ascending: false });
    setMissions((data || []) as Mission[]);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchMissions();
  }, [fetchMissions]);

  const assignMissions = useCallback(async () => {
    if (!user) return;
    await Promise.all([
      supabase.rpc("assign_daily_missions"),
      supabase.rpc("assign_weekly_missions"),
    ]);
    await fetchMissions();
  }, [user, fetchMissions]);

  const claimReward = useCallback(async (missionId: string) => {
    const { data, error } = await supabase.rpc("claim_mission_reward", {
      p_mission_id: missionId,
    });
    if (error) {
      console.error("claim_mission_reward error:", error);
      return null;
    }
    await fetchMissions();
    return data as { xp: number; coins: number };
  }, [fetchMissions]);

  const incrementProgress = useCallback(async (missionKey: string, amount = 1) => {
    const { data } = await supabase.rpc("increment_mission_progress", {
      p_mission_key: missionKey,
      p_amount: amount,
    });
    if ((data as AnyValue)?.found) {
      await fetchMissions();
    }
    return data;
  }, [fetchMissions]);

  const dailyMissions = missions.filter(m => m.mission_type === "daily");
  const weeklyMissions = missions.filter(m => m.mission_type === "weekly");
  const claimable = missions.filter(m => m.status === "completed");

  return {
    missions,
    dailyMissions,
    weeklyMissions,
    claimable,
    loading,
    assignMissions,
    claimReward,
    incrementProgress,
    refetch: fetchMissions,
  };
}
